import React from "react";
import classnames from 'classnames';
import useMouse from "beautiful-react-hooks/useMouse";
import useClientRect from "../../hooks/useClientRect";
import useLocalStorage from "../../hooks/useLocalStorage";
import ErrorBoundary from "../ErrorBoundary";
import Llama from "../Llama";
import FallingLlama from "../FallingLlama";
import Background from "../Background";
import assets from "../Llama/assets.json";
import Sidebar from "./Sidebar";
import { pick, pickIndex } from "../../utils/array";
import * as s from "./Farm.module.css";

const animations = ["static", "walking", "jump", "bounce", "fade", "vortex"];
const createLlama = (type, animationIndex, position) => ({
    type,
    position: position || { x: 0.5, y: 0.85 },
    animationIndex:
        animationIndex === undefined
            ? pickIndex(animations)
            : animationIndex
            ? animationIndex
            : 0
});

const Farm = ({ width, height }) => {
    const ref = React.useRef();
    const [position] = useMouse(ref);
    const [globalPosition, { onMouseMove }] = useMouse();
    const rect = useClientRect(ref);
    const [backgroundIndex, setBackgroundIndex] = useLocalStorage('backgroundIndex', 0);
    const [llamas, setLlamas] = useLocalStorage('llamas', [createLlama("llama")]);

    const [fallingLlamas, setFallingLlamas] = React.useState([]);
    const [draggingIndex, setDraggingIndex] = React.useState();
    const [lastMouseUpAt, setLastMouseUpAt] = React.useState(0);
    const [overflow, setOverflow] = React.useState(true);

    const mousePos = {
        x: (position.clientX - rect.x) / rect.width,
        y: (position.clientY - rect.y) / rect.height,
    };
    const globalMousePos = {
        x: (globalPosition.clientX - rect.x) / rect.width,
        y: (globalPosition.clientY - rect.y) / rect.height,
    };

    const addLlama = (type) => {
        setLlamas([...llamas, createLlama(type)]);
        return llamas.length;
    };

    const save = () => {

    }

    const addRandomLlama = () => addLlama(pick(assets).name);

    const removeLlama = (index) => {
        setFallingLlamas([...fallingLlamas, { ...llamas[index], position: {x: mousePos.x, y:mousePos.y} }]);
        setLlamas(llamas.filter((_, i) => i !== index));
    };
    const onFallDone = (index) =>
        setFallingLlamas(fallingLlamas.filter((_, i) => i !== index));

    const handleLlamaPress = (index) => {
        if (draggingIndex !== undefined) {
            return;
        }
        setDraggingIndex(index);
    };
    const handleLlamaDrag = () => {
        if (draggingIndex === undefined) {
            return;
        }
        if (globalMousePos.y > 1.2) {
            setDraggingIndex(undefined);
            removeLlama(draggingIndex);
            setOverflow(true);
        }
        else if (globalMousePos.y > 1) {
            setOverflow(false);
        } else {
            setOverflow(true);
        }
    };
    onMouseMove(handleLlamaDrag);
    const handleLlamaNextAnimation = (index) => {
        setLlamas(llamas.map((llama, i) => {
            if ( i === index) {
                llama.animationIndex = ((llamas[draggingIndex].animationIndex + 1) % animations.length);
            }
            return llama;
        }));
    }
    const handleLlamaRelease = () => {
        if (draggingIndex === undefined) {
            return;
        }
        setOverflow(true);
        // drag - update position
        setLlamas([
            ...llamas.filter((llama, i) => i !== draggingIndex),
            {
                ...llamas[draggingIndex],
                position: {
                    x: mousePos.x,
                    y: Math.min(mousePos.y, 1)
                },
            },
        ]);
        setDraggingIndex(undefined);
    };
    return (
        <ErrorBoundary componentName="Farm">
            <div className={s["root"]}>
                <div className={s["card"]}>
                    <Background
                        ref={ref}
                        className={classnames(s["stage"], !overflow && s['no-overflow'])}
                        width={width}
                        height={height}
                        onMouseUp={(e) => {
                            // double click
                            if (e.timeStamp - lastMouseUpAt < 300) {
                                handleLlamaNextAnimation(draggingIndex);
                            }
                            handleLlamaRelease();
                            setLastMouseUpAt(e.timeStamp);
                        }}
                        onMouseMove={() => handleLlamaDrag()}
                        disablePaddles={draggingIndex !== undefined}
                        background={backgroundIndex}
                        onBackgroundChange={index => setBackgroundIndex(index)}
                    >
                        {llamas.map(
                            ({ type, position:llamaPosition, animationIndex }, index) => {
                                const isDragging = draggingIndex === index;
                                const pos = isDragging ? mousePos : llamaPosition;
                                const distance = 1.2 - (1 - pos.y) * (isDragging ? 1.1 : 1);
                                const style = {
                                    top: `${pos.y * 100}%`,
                                    left: `${pos.x * 100}%`,
                                    transform: `translate(-50%, -100%) scale(${distance})`
                                };
                                return (
                                    <Llama
                                        className={s["llama"]}
                                        animation={
                                            isDragging
                                                ? "wiggle"
                                                : animations[animationIndex]
                                        }
                                        key={index}
                                        type={type}
                                        style={style}
                                        onMouseDown={() =>
                                            handleLlamaPress(index)
                                        }
                                    />
                                );
                            }
                        )}
                    </Background>
                    <Sidebar
                        className={s["sidebar"]}
                        llamaClassName={s["sidebar--llama"]}
                        onSelect={(type) => {
                            addLlama(type);
                        }}
                    />
                </div>
                <div className={s["falling-wrapper"]}>
                    {fallingLlamas.map(({ type, position:llamaPosition }, index) => {
                        return (
                            <FallingLlama
                                key={index}
                                type={type}
                                style={{ top: `20px`, left: `${llamaPosition.x * 100}%`,
                                    transform: `translate(-50%, -100%) scale(1.2)`
                                }}
                                onDone={() => onFallDone(index)}
                            />
                        );
                    })}
                </div>
            </div>
        </ErrorBoundary>
    );
};

export default Farm;
